import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import "dayjs/locale/es";
import { Button, Modal, Select, InputNumber, Spin, Empty, Input } from "antd";
import { toast } from "../../components/toasts/toastStore";
import { apiReparacionesInstance, authHeaderReparaciones } from "../../redux/actions/reparaciones/reparaciones";

import "./EventoDetallePage.css";

dayjs.locale("es");

const fmtFechaCorta = (v) => {
  if (!v) return "—";
  const d = dayjs(v);
  return d.isValid() ? d.format("D MMM YYYY, HH:mm") : "—";
};

const fmtDatetime = (v) => {
  if (!v) return "—";
  const d = dayjs(v);
  return d.isValid() ? d.format("DD/MM/YYYY, HH:mm") : "—";
};

const money = (n) => (n == null ? "—" : `$${Number(n).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

const ACTION_CONFIG = {
  CREATE: { label: "Creacion", color: "#15803d", bg: "#dcfce7" },
  UPDATE: { label: "Actualizacion", color: "#f90336", bg: "#fee2e6" },
};
const getActionConfig = (a) => ACTION_CONFIG[a] || { label: a || "Cambio", color: "#595c5e", bg: "#f1f5f9" };

const ESTADOS = { 1: "Recibido", 2: "Diagnosticado", 3: "En reparacion", 4: "Listo para entrega", 5: "Entregado", 6: "Cancelado" };

export default function ReparacionDetallePage() {
  const { idReparacion } = useParams();
  const navigate = useNavigate();

  const [reparacion, setReparacion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("datos");
  const [avanzando, setAvanzando] = useState(false);

  const [diagOpen, setDiagOpen] = useState(false);
  const [diagForm, setDiagForm] = useState({});

  const [cancelOpen, setCancelOpen] = useState(false);
  const [motivo, setMotivo] = useState("");

  const [entregarOpen, setEntregarOpen] = useState(false);
  const [formaPago, setFormaPago] = useState(1);
  const [formasPago, setFormasPago] = useState([]);

  const [variantesOpts, setVariantesOpts] = useState([]);
  const [addRefaccionOpen, setAddRefaccionOpen] = useState(false);
  const [savingRefaccion, setSavingRefaccion] = useState(false);
  const [nuevaRefaccion, setNuevaRefaccion] = useState({});

  const [actividad, setActividad] = useState([]);
  const [loadingActividad, setLoadingActividad] = useState(false);

  const fetchReparacion = useCallback(() => {
    setLoading(true);
    apiReparacionesInstance.get(`/reparaciones/${idReparacion}`, { headers: authHeaderReparaciones() })
      .then((res) => setReparacion(res.data))
      .catch(() => toast.error("No se pudo cargar la reparacion"))
      .finally(() => setLoading(false));
  }, [idReparacion]);

  useEffect(() => { fetchReparacion(); }, [fetchReparacion]);

  useEffect(() => {
    apiReparacionesInstance.get("/formas-pago", { headers: authHeaderReparaciones() })
      .then((r) => setFormasPago((r.data?.items ?? []).map((f) => ({ label: f.nombre, value: f.id_forma_pago }))))
      .catch(() => {});
    apiReparacionesInstance.get("/productos/variantes/buscar", { headers: authHeaderReparaciones() })
      .then((r) => setVariantesOpts((r.data?.items ?? []).map((v) => ({ label: `${v.producto_nombre} (${v.clave})`, value: v.id_variante, costo: parseFloat(v.precio_venta) }))))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (activeTab !== "actividad") return;
    setLoadingActividad(true);
    apiReparacionesInstance.get(`/reparaciones/${idReparacion}/actividad`, { headers: authHeaderReparaciones() })
      .then((res) => setActividad(res.data || []))
      .catch(() => {})
      .finally(() => setLoadingActividad(false));
  }, [activeTab, idReparacion]);

  const handleGuardarDiagnostico = async () => {
    if (!diagForm.diagnostico || diagForm.estimado == null) {
      toast.warning("Captura el diagnostico y el estimado");
      return;
    }
    setAvanzando(true);
    try {
      await apiReparacionesInstance.patch(`/reparaciones/${idReparacion}`, {
        tipo_aparato: reparacion.tipo_aparato, marca: reparacion.marca, modelo: reparacion.modelo,
        numero_serie: reparacion.numero_serie, falla_reportada: reparacion.falla_reportada,
        accesorios_dejados: reparacion.accesorios_dejados, fecha_prometida: reparacion.fecha_prometida,
        anticipo: reparacion.anticipo, diagnostico: diagForm.diagnostico, estimado: diagForm.estimado,
      }, { headers: authHeaderReparaciones() });
      await apiReparacionesInstance.post(`/reparaciones/${idReparacion}/estado`, { nuevo_estado: 2 }, { headers: authHeaderReparaciones() });
      toast.success("Diagnostico registrado");
      setDiagOpen(false);
      setDiagForm({});
      fetchReparacion();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo guardar");
    } finally {
      setAvanzando(false);
    }
  };

  const avanzarEstado = async (nuevo_estado) => {
    setAvanzando(true);
    try {
      await apiReparacionesInstance.post(`/reparaciones/${idReparacion}/estado`, { nuevo_estado }, { headers: authHeaderReparaciones() });
      toast.success("Estado actualizado");
      fetchReparacion();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo cambiar el estado");
    } finally {
      setAvanzando(false);
    }
  };

  const handleCancelar = async () => {
    if (!motivo.trim()) return toast.warning("Escribe un motivo de cancelacion");
    setAvanzando(true);
    try {
      await apiReparacionesInstance.post(`/reparaciones/${idReparacion}/estado`, { nuevo_estado: 6, motivo_cancelacion: motivo }, { headers: authHeaderReparaciones() });
      toast.success("Reparacion cancelada");
      setCancelOpen(false);
      setMotivo("");
      fetchReparacion();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo cancelar");
    } finally {
      setAvanzando(false);
    }
  };

  const handleEntregar = async () => {
    setAvanzando(true);
    try {
      await apiReparacionesInstance.post(`/reparaciones/${idReparacion}/estado`, { nuevo_estado: 5, id_forma_pago: formaPago }, { headers: authHeaderReparaciones() });
      toast.success("Equipo entregado y cobrado");
      setEntregarOpen(false);
      fetchReparacion();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo entregar");
    } finally {
      setAvanzando(false);
    }
  };

  const handleAddRefaccion = async () => {
    if (!nuevaRefaccion.descripcion || nuevaRefaccion.costo == null) {
      toast.warning("Descripcion y costo son obligatorios");
      return;
    }
    setSavingRefaccion(true);
    try {
      await apiReparacionesInstance.post(`/reparaciones/${idReparacion}/refacciones`, nuevaRefaccion, { headers: authHeaderReparaciones() });
      toast.success("Refaccion agregada");
      setAddRefaccionOpen(false);
      setNuevaRefaccion({});
      fetchReparacion();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo agregar");
    } finally {
      setSavingRefaccion(false);
    }
  };

  if (loading) {
    return (
      <div className="cd-main" style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 400 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!reparacion) {
    return (
      <div className="cd-main" style={{ padding: 40 }}>
        <Empty description="Reparacion no encontrada" />
      </div>
    );
  }

  const estado = reparacion.id_estado_reparacion;
  const totalRefacciones = (reparacion.refacciones || []).filter((r) => !r.cancelada).reduce((a, r) => a + parseFloat(r.costo || 0), 0);
  const restante = Math.max(parseFloat(reparacion.estimado || 0) - parseFloat(reparacion.anticipo || 0), 0);

  return (
    <div className="cd-main">
      <div className="cd-content">
        <div className="cd-header-card">
          <div className="cd-header-top">
            <div>
              <div className="cd-header-name-row">
                <h1 className="cd-client-name">{reparacion.numero_trabajo} - {reparacion.tipo_aparato.toUpperCase()}</h1>
              </div>
              <div className="cd-header-meta">
                <span className="cd-meta-item"><i className="fa-solid fa-calendar" /> Recibido el {fmtFechaCorta(reparacion.datetime)}</span>
                <span className="cd-meta-item"><i className="fa-solid fa-user" /> {reparacion.cliente_nombre}</span>
              </div>
            </div>
            <div className="cd-header-actions" style={{ flexWrap: "wrap", gap: 8 }}>
              {estado === 1 && <Button type="primary" loading={avanzando} onClick={() => setDiagOpen(true)} style={{ backgroundColor: "#f90336", borderColor: "#f90336" }}>Registrar diagnostico</Button>}
              {estado === 2 && <Button type="primary" loading={avanzando} onClick={() => avanzarEstado(3)} style={{ backgroundColor: "#f90336", borderColor: "#f90336" }}>Iniciar reparacion</Button>}
              {estado === 3 && <Button type="primary" loading={avanzando} onClick={() => avanzarEstado(4)} style={{ backgroundColor: "#f90336", borderColor: "#f90336" }}>Marcar listo</Button>}
              {estado === 4 && <Button type="primary" loading={avanzando} onClick={() => setEntregarOpen(true)} style={{ backgroundColor: "#f90336", borderColor: "#f90336" }}>Entregar y cobrar</Button>}
              {estado < 5 && <Button danger onClick={() => setCancelOpen(true)}>Cancelar</Button>}
            </div>
          </div>

          <div className="cd-header-tabs">
            <button className={`cd-tab-btn ${activeTab === "datos" ? "cd-tab-btn-active" : ""}`} onClick={() => setActiveTab("datos")}>
              <i className="fa-solid fa-file-lines" /> Informacion
            </button>
            <button className={`cd-tab-btn ${activeTab === "refacciones" ? "cd-tab-btn-active" : ""}`} onClick={() => setActiveTab("refacciones")}>
              <i className="fa-solid fa-list" /> Refacciones ({(reparacion.refacciones || []).length})
            </button>
            <button className={`cd-tab-btn ${activeTab === "actividad" ? "cd-tab-btn-active" : ""}`} onClick={() => setActiveTab("actividad")}>
              <i className="fa-solid fa-clock" /> Actividad
            </button>
          </div>
        </div>

        {estado === 6 && (
          <div className="cd-card" style={{ background: "#fee2e2", border: "1px solid #fecaca", marginBottom: 16 }}>
            <strong style={{ color: "#b91c1c" }}>Esta reparacion fue cancelada.</strong>
            <div style={{ marginTop: 6, color: "#7f1d1d" }}>{reparacion.motivo_cancelacion}</div>
            <div style={{ marginTop: 4, fontSize: 12, color: "#991b1b" }}>Por {reparacion.cancelado_por || "—"} el {fmtDatetime(reparacion.fecha_cancelacion)}</div>
          </div>
        )}

        {activeTab === "datos" && (
          <div className="cd-bento-grid" style={{ gridTemplateColumns: "2fr 1fr" }}>
            <div className="cd-card cd-card-client">
              <div className="cd-card-header">
                <div className="cd-card-icon-wrap"><i className="fa-solid fa-screwdriver-wrench"></i></div>
                <h2 className="cd-card-title">Datos del equipo</h2>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px 32px" }}>
                <div><span className="cd-field-label">Tipo de aparato</span><span className="cd-field-value">{reparacion.tipo_aparato}</span></div>
                <div><span className="cd-field-label">Marca / Modelo</span><span className="cd-field-value">{[reparacion.marca, reparacion.modelo].filter(Boolean).join(" / ") || "—"}</span></div>
                <div><span className="cd-field-label">Numero de serie</span><span className="cd-field-value">{reparacion.numero_serie || "—"}</span></div>
                <div><span className="cd-field-label">Fecha prometida</span><span className="cd-field-value">{reparacion.fecha_prometida || "—"}</span></div>
                <div style={{ gridColumn: "1 / -1" }}><span className="cd-field-label">Falla reportada</span><span className="cd-field-value" style={{ fontWeight: 400 }}>{reparacion.falla_reportada}</span></div>
                {reparacion.accesorios_dejados && <div style={{ gridColumn: "1 / -1" }}><span className="cd-field-label">Accesorios dejados</span><span className="cd-field-value" style={{ fontWeight: 400 }}>{reparacion.accesorios_dejados}</span></div>}
                {reparacion.diagnostico && <div style={{ gridColumn: "1 / -1" }}><span className="cd-field-label">Diagnostico</span><span className="cd-field-value" style={{ fontWeight: 400 }}>{reparacion.diagnostico}</span></div>}
              </div>
            </div>

            <div className="cd-card">
              <div className="cd-card-header">
                <div className="cd-card-icon-wrap"><i className="fa-solid fa-coins"></i></div>
                <h2 className="cd-card-title">Estado y costos</h2>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <div><span className="cd-field-label">Estado actual</span><span className="cd-field-value">{ESTADOS[estado]}</span></div>
                <div><span className="cd-field-label">Estimado</span><span className="cd-field-value">{money(reparacion.estimado)}</span></div>
                <div><span className="cd-field-label">Anticipo</span><span className="cd-field-value">{money(reparacion.anticipo)}</span></div>
                <div><span className="cd-field-label">Costo de refacciones</span><span className="cd-field-value">{money(totalRefacciones)}</span></div>
                {reparacion.id_venta_entrega && (
                  <div>
                    <span className="cd-field-label">Venta de entrega</span>
                    <span className="cd-field-value">
                      <a onClick={() => navigate(`/app/ventas/${reparacion.id_venta_entrega}`)} style={{ color: "#f90336", cursor: "pointer" }}>Ver venta #{reparacion.id_venta_entrega}</a>
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === "refacciones" && (
          <div className="cd-card">
            <div className="cd-card-header" style={{ justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div className="cd-card-icon-wrap"><i className="fa-solid fa-list"></i></div>
                <h2 className="cd-card-title">Refacciones utilizadas</h2>
              </div>
              {estado < 5 && (
                <Button type="primary" icon={<i className="fa-solid fa-plus" />} onClick={() => setAddRefaccionOpen(true)} style={{ backgroundColor: "#f90336", borderColor: "#f90336" }}>
                  Agregar refaccion
                </Button>
              )}
            </div>
            {(reparacion.refacciones || []).length === 0 ? (
              <Empty description="Todavia no se han agregado refacciones" />
            ) : (
              <div className="cd-pagos-list">
                {reparacion.refacciones.map((r) => (
                  <div key={r.id_refaccion_reparacion} style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 12, padding: "12px 4px", borderBottom: "1px solid #f0edf2", opacity: r.cancelada ? 0.5 : 1 }}>
                    <div><span className="cd-field-label">Descripcion</span><div className="cd-field-value">{r.producto_nombre ? `${r.producto_nombre} (${r.clave})` : r.descripcion}</div></div>
                    <div><span className="cd-field-label">Costo</span><div className="cd-field-value">{money(r.costo)}</div></div>
                    <div><span className="cd-field-label">Estado</span><div className="cd-field-value">{r.cancelada ? "Revertida" : "Activa"}</div></div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === "actividad" && (
          <div className="cd-card">
            <div className="cd-card-header">
              <div className="cd-card-icon-wrap"><i className="fa-solid fa-clock"></i></div>
              <h2 className="cd-card-title">Actividad</h2>
              <span className="cd-actividad-count">{actividad.length}</span>
            </div>
            {loadingActividad ? <Spin /> : (
              <div className="cd-actividad-list">
                {actividad.map((item, idx) => {
                  const cfg = getActionConfig(item.action);
                  return (
                    <div key={item.id_audit_log || idx} className="cd-actividad-item">
                      <div className="cd-actividad-dot-col">
                        <div className="cd-actividad-dot" style={{ background: cfg.color }} />
                        {idx < actividad.length - 1 && <div className="cd-actividad-line" />}
                      </div>
                      <div className="cd-actividad-body">
                        <div className="cd-actividad-top-row">
                          <span className="cd-actividad-badge" style={{ color: cfg.color, background: cfg.bg }}>{cfg.label}</span>
                          <span className="cd-actividad-message">{item.message}</span>
                        </div>
                        <div className="cd-actividad-bottom-row">
                          <span className="cd-actividad-time">{fmtDatetime(item.datetime)}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      <Modal open={diagOpen} onCancel={() => setDiagOpen(false)} onOk={handleGuardarDiagnostico} okText="Guardar diagnostico" cancelText="Cancelar" title="Registrar diagnostico" confirmLoading={avanzando} centered okButtonProps={{ style: { background: "#f90336", borderColor: "#f90336" } }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <Input.TextArea rows={3} placeholder="Diagnostico" value={diagForm.diagnostico || ""} onChange={(e) => setDiagForm((f) => ({ ...f, diagnostico: e.target.value }))} />
          <InputNumber style={{ width: "100%" }} placeholder="Costo estimado" min={0} precision={2} value={diagForm.estimado} onChange={(v) => setDiagForm((f) => ({ ...f, estimado: v }))} />
        </div>
      </Modal>

      <Modal open={cancelOpen} onCancel={() => setCancelOpen(false)} onOk={handleCancelar} okText="Cancelar reparacion" okButtonProps={{ danger: true }} cancelText="Volver" title="Cancelar reparacion" confirmLoading={avanzando} centered>
        <Input.TextArea rows={3} placeholder="Motivo de cancelacion" value={motivo} onChange={(e) => setMotivo(e.target.value)} />
      </Modal>

      <Modal open={entregarOpen} onCancel={() => setEntregarOpen(false)} onOk={handleEntregar} okText="Confirmar entrega y cobro" cancelText="Cancelar" title="Entregar y cobrar" confirmLoading={avanzando} centered okButtonProps={{ style: { background: "#f90336", borderColor: "#f90336" } }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div>Restante a cobrar: <strong>{money(restante)}</strong></div>
          {restante > 0 && (
            <Select options={formasPago} value={formaPago} onChange={setFormaPago} placeholder="Forma de pago" />
          )}
        </div>
      </Modal>

      <Modal open={addRefaccionOpen} onCancel={() => setAddRefaccionOpen(false)} onOk={handleAddRefaccion} okText="Agregar" cancelText="Cancelar" title="Agregar refaccion" confirmLoading={savingRefaccion} centered okButtonProps={{ style: { background: "#f90336", borderColor: "#f90336" } }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <Select
            placeholder="Producto de inventario (opcional)"
            options={variantesOpts}
            allowClear
            showSearch
            optionFilterProp="label"
            onChange={(v, opt) => setNuevaRefaccion((f) => ({ ...f, id_variante: v, costo: opt?.costo ?? f.costo }))}
          />
          <Input placeholder="Descripcion de la refaccion" value={nuevaRefaccion.descripcion || ""} onChange={(e) => setNuevaRefaccion((f) => ({ ...f, descripcion: e.target.value }))} />
          <InputNumber style={{ width: "100%" }} placeholder="Costo" min={0} precision={2} value={nuevaRefaccion.costo} onChange={(v) => setNuevaRefaccion((f) => ({ ...f, costo: v }))} />
        </div>
      </Modal>
    </div>
  );
}
